import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { AppLayout } from '@/components/app-layout';
import { RouteError } from '@/components/route-error';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { meQueryOptions } from '@/lib/queries';
import { AgentEditorPage } from '@/pages/agent-editor';
import { AgentsPage } from '@/pages/agents';
import { ApiKeysPage } from '@/pages/api-keys';
import { EndpointDetailPage } from '@/pages/endpoint-detail';
import { EndpointsPage } from '@/pages/endpoints';
import { EnvironmentPage } from '@/pages/environment';
import { SkillsPage } from '@/pages/skills';
import { WorkspacesPage } from '@/pages/workspaces';
import { ToolsPage } from '@/pages/tools';
import { AuditLogPage } from '@/pages/audit-log';
import { InvitePage } from '@/pages/invite';
import { LlmSetupPage } from '@/pages/llm-setup';
import { LoginPage } from '@/pages/login';
import { MembersPage } from '@/pages/members';
import { SettingsPage } from '@/pages/settings';
import { NotFoundPage } from '@/pages/not-found';
import { OverviewPage } from '@/pages/overview';
import { PlaygroundPage } from '@/pages/playground';
import { UsagePage } from '@/pages/usage';
import { CachingPage } from '@/pages/caching';
import { TenantsPage } from '@/pages/tenants';
import { MetricsPage } from '@/pages/metrics';
import { PromptDetailPage, PromptsPage } from '@/pages/prompts';
import { RequestContextPage } from '@/pages/request-context';
import { SandboxDetailPage, SandboxesPage } from '@/pages/sandboxes';
import { ScorersPage } from '@/pages/scorers';
import {
  RunTracePage,
  ThreadPage,
  TRACE_TABS,
  TracesPage,
  WorkflowTracePage,
} from '@/pages/traces';
import { SessionsPage } from '@/pages/sessions';
import { ConversationsPage } from '@/pages/conversations';
import { WorkflowDetailPage, WorkflowsPage } from '@/pages/workflows';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <TooltipProvider>
      <Outlet />
      <Toaster richColors position="top-right" />
    </TooltipProvider>
  ),
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: z.object({ redirect: z.string().optional(), error: z.string().optional() }),
  beforeLoad: async ({ context }) => {
    if (await context.queryClient.ensureQueryData(meQueryOptions)) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InvitePage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { me };
  },
  component: AppLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: OverviewPage,
});
const llmSetupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/llm-setup',
  component: LlmSetupPage,
});
const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents',
  component: AgentsPage,
});
const agentEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/agents/$agentId',
  component: AgentEditorPage,
});
const playgroundRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/playground',
  validateSearch: z.object({ agentId: z.string().optional() }),
  component: PlaygroundPage,
});
const endpointsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/endpoints',
  component: EndpointsPage,
});
const endpointDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/endpoints/$endpointId',
  component: EndpointDetailPage,
});
const apiKeysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/api-keys',
  component: ApiKeysPage,
});
const toolsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tools',
  component: ToolsPage,
});
const skillsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/skills',
  component: SkillsPage,
});
const workspacesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workspaces',
  component: WorkspacesPage,
});
const environmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/environment',
  component: EnvironmentPage,
});
// Runs and their traces are one page now; old links keep working.
const runsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/runs',
  beforeLoad: () => {
    throw redirect({ to: '/traces', search: { tab: 'runs' } });
  },
});
const runDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/runs/$runId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/traces/runs/$runId', params: { runId: params.runId } });
  },
});
const usageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/usage',
  component: UsagePage,
});
const cachingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/caching',
  component: CachingPage,
});
const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/members',
  component: MembersPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  component: SettingsPage,
});
const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit-log',
  component: AuditLogPage,
});
const workflowsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workflows',
  component: WorkflowsPage,
});
const workflowDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workflows/$workflowId',
  component: WorkflowDetailPage,
});
const promptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/prompts',
  component: PromptsPage,
});
const promptDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/prompts/$promptId',
  component: PromptDetailPage,
});
const requestContextRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/request-context',
  component: RequestContextPage,
});
const sandboxesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sandboxes',
  component: SandboxesPage,
});
const sandboxDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sandboxes/$sandboxId',
  component: SandboxDetailPage,
});
const scorersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/scorers',
  component: ScorersPage,
});
const tracesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/traces',
  validateSearch: z.object({ tab: z.enum(TRACE_TABS).optional() }),
  component: TracesPage,
});
const runTraceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/traces/runs/$runId',
  component: RunTracePage,
});
const threadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/traces/threads',
  validateSearch: z.object({
    agentId: z.string(),
    threadId: z.string(),
    apiKeyId: z.string().optional(),
    userId: z.string().optional(),
  }),
  component: ThreadPage,
});
const workflowTraceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/traces/workflows/$runId',
  component: WorkflowTracePage,
});
const sessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sessions',
  component: SessionsPage,
});
const conversationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/conversations',
  component: ConversationsPage,
});
const metricsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/metrics',
  component: MetricsPage,
});

const tenantsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/platform/tenants',
  beforeLoad: ({ context }) => {
    if (!context.me.user.isSuperAdmin) throw redirect({ to: '/' });
  },
  component: TenantsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  appRoute.addChildren([
    overviewRoute,
    llmSetupRoute,
    agentsRoute,
    agentEditorRoute,
    playgroundRoute,
    endpointsRoute,
    endpointDetailRoute,
    apiKeysRoute,
    toolsRoute,
    skillsRoute,
    workspacesRoute,
    environmentRoute,
    runsRoute,
    runDetailRoute,
    usageRoute,
    cachingRoute,
    membersRoute,
    settingsRoute,
    auditRoute,
    workflowsRoute,
    workflowDetailRoute,
    promptsRoute,
    promptDetailRoute,
    requestContextRoute,
    sandboxesRoute,
    sandboxDetailRoute,
    scorersRoute,
    tracesRoute,
    sessionsRoute,
    conversationsRoute,
    runTraceRoute,
    threadRoute,
    workflowTraceRoute,
    metricsRoute,
    tenantsRoute,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultErrorComponent: RouteError,
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
